import {useQuery} from '@tanstack/react-query';
import {
    BioMatchEnrollmentService,
    type CuratedCard,
    type CustomLabPackage,
    type CustomLabPackageProduct,
} from '../services/bioMatchEnrollmentService';

/**
 * Shared BioMatch package/product derivation for the kit hero.
 *
 * Historically this hook also computed per-marker severity for the
 * "Biomarkers Need Attention" card; that card now derives from the
 * patient's full results via `useAbnormalBiomarkers` (patient-dashboard
 * hooks), so only the package/product outputs remain here.
 *
 * The React Query key matches the hero's original query so any future
 * consumers share a single request.
 */

export interface BioMatchMarkersResult {
    isLoading: boolean;
    isError: boolean;
    customPackage: CustomLabPackage | null;
    /** Curated recommendation cards. Currently gated off in the hero
     *  (SHOW_CURATED_CARDS) but still served by the API. */
    curatedCards: CuratedCard[];
    /** Org member discount percent (0-100); null = none. */
    memberDiscountPct: number | null;
    /** The matched product the BioMatch card sells. The card's name,
     *  description and price all come from this row, so no product means
     *  no card. */
    product: CustomLabPackageProduct | null;
    /** Assays bundled by the matched product — the "Included In Your Panel"
     *  list. Describes what the kit tests, not the patient's results. */
    productIncludedAssays: Array<{code: string; name: string; loinc: string | null}>;
}

/** Query key for the assembled dashboard view. Exported so checkout flows
 *  can invalidate it — a kit purchase consumes the offer server-side (the
 *  hero suppresses while the order is in flight) and the dashboard should
 *  reflect that immediately, not after the 60s staleTime. */
export const BIOMATCH_DASHBOARD_QUERY_KEY = ['biomatch', 'dashboard', 'mine'] as const;

export function useBioMatchMarkers(): BioMatchMarkersResult {
    const {data, isLoading, isError} = useQuery({
        queryKey: BIOMATCH_DASHBOARD_QUERY_KEY,
        queryFn: () => BioMatchEnrollmentService.getMyAssembled(),
        staleTime: 60 * 1000,
    });

    const customPackage = data?.custom_package ?? null;

    return {
        isLoading,
        isError,
        customPackage,
        curatedCards: data?.curated_cards ?? [],
        memberDiscountPct: data?.mylabs_plus_member_discount_pct ?? null,
        product: customPackage?.products?.[0] ?? null,
        productIncludedAssays: customPackage?.product_included_assays ?? [],
    };
}
